import { Fragment } from "react";
import { useState, useEffect, useContext } from "react";
import { Container, Dropdown, DropdownButton, Nav, Navbar, NavDropdown, Offcanvas } from "react-bootstrap";

import "./side-bar.scss";
import { BsPersonCircle } from "react-icons/bs";
import { CiLogout } from "react-icons/ci";
import { GoOrganization } from "react-icons/go";
import { MdOutlineKeyboardArrowDown, MdOutlineKeyboardArrowRight } from "react-icons/md";

import { getBaseUrl, getManagerUrl } from "@/config";

import { customHistory } from "../../../custom-browser-router";
import SearchInput from "../../../shared/components/search-input/search-input";
import Tooltip from "../../../shared/components/tooltip/tooltip";
import AuthContext, { ISelectedTenant, IUserTenant } from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import {useHttpNonTable} from "../../../shared/hooks/use-httpNonTable";
import { getUser, getAllDataNonTable } from "../../../shared/utils/api";



export interface ITab {
  project: string | undefined,
  link: string;
  displayName: string;
  icon?: JSX.Element;
  amount?: number;
}

export interface IDropdownTab {
  title: string;
  tabs: ITab[];
  icon?: JSX.Element;
}

export interface ISideBarConfig {
  tabs: (ITab | IDropdownTab)[];
}

function determineIfIsDropdownTab(dropdownTab: IDropdownTab | ITab): dropdownTab is IDropdownTab {
  return !!(dropdownTab as IDropdownTab).tabs;
}
const SideBar = (props: ISideBarConfig) => {
  const [data, setData] = useState<ISelectedTenant>({selected_tenant: "", selected_tenant_id: "", roles: [], tenants: []});
  const [openMenuItems, setOpenMenuItems] = useState<string[]>([]);

  const authCtx = useContext(AuthContext);
  
  const { sendRequest: fetchUser, status: userStatus, data: loadedUser, error: errorUserGet } = useHttp(getUser, false, authCtx.token);
  const { sendRequest: updateUser, status: updateUserStatus, data: updatedLoadedUser, error: errorUpdateUser } = useHttp(getUser, false, authCtx.token);

  const { sendRequest: getAnalyticsOnboarding, status: analyticsOnboardingStatus, data: analyticsOnboardingData, error: analyticsOnboardingError } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getPlatformOnboarding, status: platformOnboardingStatus, data: platformOnboardingData, error: platformOnboardingError } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined);
  const { sendRequest: getManagerOnboarding, status: managerOnboardingStatus, data: managerOnboardingData, error: managerOnboardingError } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "manager");

  useEffect(() => {
    fetchUser("whoami");
  }, [authCtx.userId]);

  useEffect(() => {
    if (authCtx.selectedTenant.selected_tenant_id && getBaseUrl() !== "") {
      getAnalyticsOnboarding("onboarding");
    }
  }, [authCtx.onboardingAnalyticsToggle, authCtx.selectedTenant.selected_tenant_id]);

  useEffect(() => {
    if (authCtx.selectedTenant.selected_tenant_id && getManagerUrl() !== "") {
      getManagerOnboarding("onboarding");
    }
  }, [authCtx.onboardingManagerToggle, authCtx.selectedTenant.selected_tenant_id]);

  useEffect(() => {
    if (authCtx.selectedTenant.selected_tenant_id) {
      getPlatformOnboarding("onboarding");
    }
  }, [authCtx.onboardingPlatformToggle, authCtx.selectedTenant.selected_tenant_id]);

  useEffect(() => {
    if (platformOnboardingStatus === "completed" && !platformOnboardingError) {
      authCtx.setOnboardingPlatformHandler(platformOnboardingData);
    }
  }, [platformOnboardingStatus, platformOnboardingError]);

  useEffect(() => {
    if (analyticsOnboardingStatus === "completed" && !analyticsOnboardingError) {
      authCtx.setOnboardingAnalyticsHandler(analyticsOnboardingData);
    }
  }, [analyticsOnboardingStatus, analyticsOnboardingError]);

  useEffect(() => {
    if (managerOnboardingStatus === "completed" && !managerOnboardingError) {
      authCtx.setOnboardingManagerHandler(managerOnboardingData);
    }
  }, [managerOnboardingStatus, managerOnboardingError]);

  useEffect(() => {
    if (authCtx.userUpdateStatus) {
      updateUser("whoami");
    }
  }, [authCtx.userUpdateStatus]);

  useEffect(() => {
    if (updateUserStatus === "completed" && !errorUpdateUser) {
      const findTenants = updatedLoadedUser.tenants.filter((tenant: IUserTenant) => authCtx.selectedTenant.selected_tenant_id === tenant.id);
      // if the user deleted itself from the selected tenant
      if (findTenants.length === 0 && updatedLoadedUser.tenants.length !== 0) {
        setData({selected_tenant: updatedLoadedUser.tenants[0].name, selected_tenant_id: updatedLoadedUser.tenants[0].id, roles: updatedLoadedUser.tenants[0].roles, tenants: updatedLoadedUser.tenants});
        authCtx.selectTenant(updatedLoadedUser.tenants[0].id, updatedLoadedUser.tenants[0].name, updatedLoadedUser.tenants[0].roles);
        authCtx.userUpdate();
      }
      else if (findTenants.length === 0 && updatedLoadedUser.tenants.length === 0) {
        // deleted himself from the only existing tenant the user is part of
        setData({selected_tenant: "", selected_tenant_id: "", roles: [], tenants: []});
        authCtx.selectTenant("", "", []);
        authCtx.userUpdate();
      }
      else {
        // post/edit
        setData({selected_tenant: findTenants[0].name, selected_tenant_id: findTenants[0].id, roles: findTenants[0].roles, tenants: updatedLoadedUser.tenants});
        authCtx.selectTenant(findTenants[0].id, findTenants[0].name, findTenants[0].roles);
        authCtx.userUpdate();
      }
    }
  }, [updateUserStatus]);
  
  useEffect(() => {
    if (userStatus === "completed" && !errorUserGet && loadedUser && loadedUser.length !== 0) {
      authCtx.selectUserData(loadedUser.admin, loadedUser.email, loadedUser.name, loadedUser.last_name, loadedUser.tenants);
      if (authCtx.selectedTenant.selected_tenant_id && authCtx.selectedTenant.selected_tenant) {
        // on page refresh
        setData({selected_tenant: authCtx.selectedTenant.selected_tenant, selected_tenant_id: authCtx.selectedTenant.selected_tenant_id, roles: authCtx.selectedTenant.roles, tenants: loadedUser.tenants});
      }
      else {
        // after login select [0] by default
        loadedUser.tenants[0] && setData({selected_tenant: loadedUser.tenants[0].name, selected_tenant_id: loadedUser.tenants[0].id, roles: loadedUser.tenants[0].roles, tenants: loadedUser.tenants});
      }
    }
  }, [userStatus]);

  useEffect(() => {
    if (data.selected_tenant_id && authCtx.selectedTenant.selected_tenant_id !== data.selected_tenant_id) {
      authCtx.selectTenant(data.selected_tenant_id, data.selected_tenant, data.roles);
    }
  }, [data.selected_tenant_id]);

  useEffect(() => {    
    if (data.selected_tenant_id === authCtx.selectedTenant.selected_tenant_id && data.selected_tenant_id && authCtx.selectedTenant.selected_tenant_id) {
      customHistory.push("/");
    }
  }, [authCtx.selectedTenant.selected_tenant_id]);

  const renderTabs = (tabData: ITab | IDropdownTab) => {
    if (determineIfIsDropdownTab(tabData)) {
      const arrowData = openMenuItems.includes(tabData.title);
      const hasEmptyChild = tabData.tabs.some((tab: ITab) => tab.amount === 0);

      const hasEmptyFilteredChild = tabData.tabs.filter((tab: ITab) => tab.displayName !== "Datamodel reports" && tab.displayName !== "Report configurations" && tab.displayName !== "Lists").some((tab: ITab) => tab.amount === 0);
  
      const shouldShowRedDot = hasEmptyChild && hasEmptyFilteredChild && !arrowData && tabData.title !== "Technical config";
      const onItemClickHandler = (title: string) => {
        setOpenMenuItems((prev) => {
          if (prev.includes(title)) {
            return prev.filter((item) => item !== title);
          } else {
            return [...prev, title];
          }
        });
      };

      return (
        <NavDropdown
          show={arrowData}
          onClick={() => onItemClickHandler(tabData.title)}
          key={tabData.title}
          title={<Fragment>{arrowData ? <MdOutlineKeyboardArrowDown /> : <MdOutlineKeyboardArrowRight />}{tabData.title}{shouldShowRedDot ? (!arrowData ?<span className="sideBar-red-dot"></span>: "") : ""}</Fragment>}
        >
          {tabData.tabs.map((tab) => {
            let defineAmountLabel;
            if (tab.amount !== undefined && tab.amount !== 0 ) {
              defineAmountLabel = <span className="sideBar-menu-tab-status">{tab.amount}</span>;
            } else if (tab.amount === 0) {
              if (tab.link === "/lists" || tab.link === "/database-connection" || tab.link === "/datamodel-reports" || tab.link === "/datamodel-reports/configurations" || tab.link === "/tokens") {
                defineAmountLabel = <span className="sideBar-menu-tab-status">{tab.amount}</span>;
              } else {
                defineAmountLabel = <Tooltip content={"More data is required"} direction="top" delay={350}  ><span className="sideBar-menu-tab-status-red">!</span></Tooltip>;
              }
            }
            return (
              <NavDropdown.Item
                eventKey={tab.displayName}
                title={tab.displayName}
                onSelect={() => console.log(tab.displayName)}
                className={"sideBar-menu-subtab"}
                key={tab.link}
                onClick={() => {
                  onItemClickHandler(tabData.title);
                  customHistory.push(`${tab.link}`);
                }}
              >
                {tab.icon && tab.icon}
                {tab.displayName}
                {defineAmountLabel}
              </NavDropdown.Item>
            );
          })}
        </NavDropdown>
      );
    }
    return <Nav.Link className="sideBar-menu-tab" key={tabData.link} eventKey={tabData.displayName} onSelect={() => console.log(tabData.displayName)} onClick={() => {customHistory.push(`${tabData.link}`);}}>{tabData.icon && tabData.icon}{tabData.displayName}{tabData.amount !== undefined? <span className={`sideBar-menu-tab-status${tabData.amount === 0? "-red" : ""}`}>{tabData.amount === 0? "!" : tabData.amount}</span> : ""}</Nav.Link>;
  };

  const handleOnChange = (targetVal: string, inputKey: string, id: string) => {
    if (loadedUser) {
      const matchedTenantWithRoles = loadedUser && loadedUser.tenants.filter((tenant: IUserTenant) => tenant.id === id);
      const matchedTenantWithRolesUpdate = updatedLoadedUser && updatedLoadedUser.tenants.filter((tenant: IUserTenant) => tenant.id === id);
      if (matchedTenantWithRolesUpdate) {
        setData(prevState => ({ ...prevState, [inputKey]: targetVal, selected_tenant_id: id, roles: matchedTenantWithRolesUpdate[0]? matchedTenantWithRolesUpdate[0].roles : [] }));
      } else {
        setData(prevState => ({ ...prevState, [inputKey]: targetVal, selected_tenant_id: id, roles: matchedTenantWithRoles[0]? matchedTenantWithRoles[0].roles : [] }));
      }
    }
  };
  
  return (
    <div className="sideBar">
      <Navbar collapseOnSelect key="false" expand="false" className="sideBar-navbar">
        <div className="sideBar-header">
          <div className="sideBar-switchButon">
            <Navbar.Toggle aria-controls={"offcanvasNavbar-expand-false"}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                fill="white"
                className="bi bi-list"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
                />
              </svg>
            </Navbar.Toggle>
            <img
              onClick={() => customHistory.push("/")}
              src="/images/main-logo.png"
              width="100"
              height="40"
              className="sideBar-logo"
              alt="Sygno"
            />
          </div>
          <div className="sideBar-actions">
            <SearchInput
              defaultValue={data.selected_tenant}
              handleOnChange={handleOnChange}
              data={(loadedUser || updatedLoadedUser) && data.tenants}
              inputKey="selected_tenant"
              placeholder={"Select a tenant"}
              id={data.selected_tenant_id}
              mode="simple"
              label="groups"
              required={true}
              dataType="groups"
              noRoleCheck={true}
              onFocusEmpty={true}
              showInputIcons={<GoOrganization />}
            />
            <DropdownButton
              className="sideBar-userDropdown"
              title={
                <div className="sideBar-role">
                  <div className="sideBar-name">
                    {updatedLoadedUser?`${updatedLoadedUser.name} ${updatedLoadedUser.last_name}` : (loadedUser && `${loadedUser.name} ${loadedUser.last_name}`)}
                  </div>
                  <BsPersonCircle />
                </div>
              }
              id="input-group-dropdown-2"
            >
              <span className="sideBar-accountDetails">
                <div className="sideBar-account">{loadedUser && `${loadedUser.name} ${loadedUser.last_name}`}</div>
                <div className="sideBar-role-label">
                  <span className="sideBar-accountName">{loadedUser && loadedUser.email}</span>
                  {data.roles && data.roles.map((role,i) => <span key={i} className="sideBar-roleLabel">{role}</span>)}
                </div>
                
                <Dropdown.Item onClick={() => {
                  customHistory.push(`/change-password/${authCtx.userId}`);
                }} className="sideBar-changePassword">
                  Change password
                </Dropdown.Item>
              </span>
              <Dropdown.Item
                className="sideBar-logout"
                onClick={() => {
                  authCtx.logout();
                  customHistory.push("/login");
                }}
              >
                <span>Sign out</span><span className="sideBar-logOutIco"><CiLogout /></span>
              </Dropdown.Item>
            </DropdownButton>
          </div>
        </div>
        <Container className="sideBar-container">
          <Navbar.Offcanvas
            id={"offcanvasNavbar-expand-false"}
            aria-labelledby={"offcanvasNavbarLabel-expand-false"}
            placement="start"
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title id={"offcanvasNavbarLabel-expand-false"}>
                <span onClick={() => {
                  customHistory.push("/");
                }} className="sideBar-brand">
                  Sygno
                </span>
              </Offcanvas.Title>
            </Offcanvas.Header>

            <NavDropdown.Divider />

            <Offcanvas.Body>
              <div role="navigation">
                <Nav className="sibebarMenu justify-content-end flex-grow-1">
                  {props.tabs.map(renderTabs)}
                </Nav>
              </div>

            </Offcanvas.Body>
            <div className="sideBar-disclaimer">Powered by Sygno®</div>
          </Navbar.Offcanvas>
        </Container>
      </Navbar>
    </div>
  ) ;
};

export default SideBar;